import { Component, type ReactNode } from "react";
import { QueryState } from "@/components/query-state";

/**
 * The last thing between a render crash and a white page.
 *
 * A blank screen is the worst failure this application can show: it says
 * nothing, it cannot be reported usefully, and it is indistinguishable from a
 * network problem or a bad deploy. Anything is better, so long as it says what
 * happened and offers a way out.
 *
 * Scoped per route rather than wrapped once around everything, so a crash in a
 * drawer does not take the navigation down with it — whoever hits one can still
 * go somewhere else instead of reloading.
 */
interface Props {
  children: ReactNode;
  /** Changing this resets the boundary, so navigating away clears a crash. */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidUpdate(previous: Props): void {
    if (previous.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  override componentDidCatch(error: Error): void {
    // The console is where a developer will look, and the message is the one
    // thing a person can usefully repeat back.
    console.error("render failed", error);
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;

    return <QueryState error={this.state.error} onRetry={() => this.setState({ error: null })} />;
  }
}
